import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { Box, Typography } from '@mui/material';
import { TOPIC_COLORS, TOPIC_NAMES } from '../vectorSpace/topics';

interface Vector3DPoint {
  id: string;
  x: number;
  y: number;
  z: number;
  topic: string;
  document: string;
}

export interface VectorSpace3DHandle {
  resetCamera: () => void;
  centerView: () => void;
}

interface VectorSpace3DProps {
  points: Vector3DPoint[];
  onPointHover?: (point: Vector3DPoint | null) => void;
  theme?: 'dark' | 'light';
  visibleTopics?: Set<string>;
  showLinks?: boolean;
}

const THEME_CONFIG = {
  dark: {
    bg: 0x0d0d14,
    border: 0x2a2a3d,
    axis: 0x6b6b8a,
    link: 0x8b8bb0,
  },
  light: {
    bg: 0xffffff,
    border: 0xe0e0e0,
    axis: 0x999999,
    link: 0x666680,
  },
};

const DEFAULT_VISIBLE = new Set(TOPIC_NAMES);

// Zoom limits (camera distance from the focus point).
const MIN_DISTANCE = 0.02;
const MAX_DISTANCE = 60;
const DEFAULT_SPHERICAL = { t: 0, p: Math.PI / 3, r: 8 };

// Point size and hover radius are in screen pixels so points stay pickable at any zoom.
const POINT_SIZE_PX = 6;
const HOVER_RADIUS_PX = 8;

// Each point links to its K nearest neighbours; skipped above MAX_LINK_POINTS (O(n²)).
const LINK_NEIGHBOURS = 2;
const MAX_LINK_POINTS = 5000;

const buildNeighbourLinks = (pts: Vector3DPoint[]): Float32Array => {
  const n = pts.length;
  if (n < 2 || n > MAX_LINK_POINTS) return new Float32Array(0);

  const seen = new Set<number>();
  const segments: number[] = [];

  for (let i = 0; i < n; i++) {
    // Keep the K closest candidates in a tiny sorted list.
    const best: { j: number; d: number }[] = [];
    for (let j = 0; j < n; j++) {
      if (j === i) continue;
      const dx = pts[i].x - pts[j].x;
      const dy = pts[i].y - pts[j].y;
      const dz = pts[i].z - pts[j].z;
      const d = dx * dx + dy * dy + dz * dz;
      if (best.length < LINK_NEIGHBOURS || d < best[best.length - 1].d) {
        best.push({ j, d });
        best.sort((a, b) => a.d - b.d);
        if (best.length > LINK_NEIGHBOURS) best.pop();
      }
    }
    for (const { j } of best) {
      const key = i < j ? i * n + j : j * n + i;
      if (seen.has(key)) continue;
      seen.add(key);
      segments.push(pts[i].x, pts[i].y, pts[i].z, pts[j].x, pts[j].y, pts[j].z);
    }
  }
  return new Float32Array(segments);
};

export const VectorSpace3D = React.forwardRef<VectorSpace3DHandle, VectorSpace3DProps>(
  (
    {
      points,
      onPointHover,
      theme = 'dark',
      visibleTopics = DEFAULT_VISIBLE,
      showLinks = false,
    },
    ref
  ) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const pointsRef = useRef<THREE.Points | null>(null);
  const linksRef = useRef<THREE.LineSegments | null>(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const visiblePointsRef = useRef<Vector3DPoint[]>([]);
  const sphericalRef = useRef({ ...DEFAULT_SPHERICAL });
  const panRef = useRef({ x: 0, y: 0, z: 0 });
  const dragRef = useRef({ isDragging: false, isPanning: false, moved: false, lastX: 0, lastY: 0 });
  const animationIdRef = useRef<number>();
  const updateCameraRef = useRef<() => void>(() => {});
  const hoveredIndexRef = useRef<number>(-1);
  const onPointHoverRef = useRef(onPointHover);
  onPointHoverRef.current = onPointHover;

  const [tooltip, setTooltip] = useState<{ point: Vector3DPoint; x: number; y: number } | null>(null);

  React.useImperativeHandle(ref, () => ({
    resetCamera: () => {
      sphericalRef.current = { ...DEFAULT_SPHERICAL };
      panRef.current = { x: 0, y: 0, z: 0 };
      updateCameraRef.current();
    },
    centerView: () => {
      panRef.current = { x: 0, y: 0, z: 0 };
      updateCameraRef.current();
    },
  }));

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    const themeConfig = THEME_CONFIG[theme] || THEME_CONFIG.dark;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.001, 1000);
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, premultipliedAlpha: true });
    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;

    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x000000, 0);
    renderer.setSize(width, height);

    const updateCamera = () => {
      const { t, p, r } = sphericalRef.current;
      const { x, y, z } = panRef.current;

      camera.position.set(
        r * Math.sin(p) * Math.sin(t) + x,
        r * Math.cos(p) + y,
        r * Math.sin(p) * Math.cos(t) + z
      );
      camera.lookAt(x, y, z);
      camera.updateMatrixWorld();
    };
    updateCameraRef.current = updateCamera;
    updateCamera();

    const clearHover = () => {
      if (hoveredIndexRef.current !== -1) {
        hoveredIndexRef.current = -1;
        setTooltip(null);
        onPointHoverRef.current?.(null);
      }
    };

    const pickPoint = (e: MouseEvent) => {
      const points3d = pointsRef.current;
      if (!points3d) return clearHover();

      const rect = canvas.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );

      // Convert the pixel hover radius to world units at the focus distance.
      const worldPerPixel = (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * sphericalRef.current.r) / rect.height;
      raycasterRef.current.params.Points.threshold = HOVER_RADIUS_PX * worldPerPixel;
      raycasterRef.current.setFromCamera(ndc, camera);

      const hit = raycasterRef.current.intersectObject(points3d)[0];
      const index = hit?.index ?? -1;
      const point = index >= 0 ? visiblePointsRef.current[index] : undefined;

      if (!point) return clearHover();

      hoveredIndexRef.current = index;
      setTooltip({ point, x: e.clientX - rect.left, y: e.clientY - rect.top });
      onPointHoverRef.current?.(point);
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button === 1 || (e.button === 0 && e.shiftKey)) {
        e.preventDefault();
        dragRef.current.isPanning = true;
      } else if (e.button === 0) {
        dragRef.current.isDragging = true;
      }
      dragRef.current.moved = false;
      dragRef.current.lastX = e.clientX;
      dragRef.current.lastY = e.clientY;
    };

    const endDrag = () => {
      dragRef.current.isDragging = false;
      dragRef.current.isPanning = false;
    };

    const onMouseMove = (e: MouseEvent) => {
      const drag = dragRef.current;
      const dx = e.clientX - drag.lastX;
      const dy = e.clientY - drag.lastY;

      if (drag.isPanning) {
        // Pan along the camera's own axes; speed scales with zoom so it feels constant.
        const rect = canvas.getBoundingClientRect();
        const worldPerPixel = (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * sphericalRef.current.r) / rect.height;
        const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
        const up = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1);
        panRef.current.x += (-dx * right.x + dy * up.x) * worldPerPixel;
        panRef.current.y += (-dx * right.y + dy * up.y) * worldPerPixel;
        panRef.current.z += (-dx * right.z + dy * up.z) * worldPerPixel;
        drag.lastX = e.clientX;
        drag.lastY = e.clientY;
        drag.moved = true;
        updateCamera();
        clearHover();
      } else if (drag.isDragging) {
        sphericalRef.current.t -= dx * 0.008;
        sphericalRef.current.p = Math.max(0.1, Math.min(Math.PI - 0.1, sphericalRef.current.p + dy * 0.008));
        drag.lastX = e.clientX;
        drag.lastY = e.clientY;
        drag.moved = true;
        updateCamera();
        clearHover();
      } else {
        pickPoint(e);
      }
    };

    const onMouseLeave = () => {
      endDrag();
      clearHover();
    };

    // Exponential zoom so each scroll notch is a constant fraction, from wide view down to a single point.
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const s = sphericalRef.current;
      s.r = Math.max(MIN_DISTANCE, Math.min(MAX_DISTANCE, s.r * Math.exp(e.deltaY * 0.0015)));
      updateCamera();
      clearHover();
    };

    // Double-click a point to re-centre on it, so zooming in keeps it under the focus.
    const onDoubleClick = () => {
      const point = visiblePointsRef.current[hoveredIndexRef.current];
      if (!point) return;
      panRef.current = { x: point.x, y: point.y, z: point.z };
      updateCamera();
    };

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mouseup', endDrag);
    canvas.addEventListener('mouseleave', onMouseLeave);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('dblclick', onDoubleClick);

    const axisLength = 2;
    const axes: [number[], number[]][] = [
      [[-axisLength, 0, 0], [axisLength, 0, 0]],
      [[0, -axisLength, 0], [0, axisLength, 0]],
      [[0, 0, -axisLength], [0, 0, axisLength]],
    ];
    const axisMaterial = new THREE.LineBasicMaterial({ color: themeConfig.axis, transparent: true, opacity: 0.4 });
    const axisGeometries = axes.map(([start, end]) => {
      const geom = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(...(start as [number, number, number])),
        new THREE.Vector3(...(end as [number, number, number])),
      ]);
      scene.add(new THREE.Line(geom, axisMaterial));
      return geom;
    });

    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      const newWidth = canvas.clientWidth;
      const newHeight = canvas.clientHeight;

      if (newWidth > 0 && newHeight > 0) {
        camera.aspect = newWidth / newHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(newWidth, newHeight);
        renderer.setPixelRatio(window.devicePixelRatio);
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(canvas);
    window.addEventListener('resize', handleResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mouseup', endDrag);
      canvas.removeEventListener('mouseleave', onMouseLeave);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('dblclick', onDoubleClick);
      if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
      axisGeometries.forEach(g => g.dispose());
      axisMaterial.dispose();
      pointsRef.current = null;
      linksRef.current = null;
      renderer.dispose();
    };
  }, [theme]);

  // Points + optional nearest-neighbour links. Re-runs on theme because the scene is rebuilt with it.
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    if (pointsRef.current) {
      scene.remove(pointsRef.current);
      pointsRef.current.geometry.dispose();
      (pointsRef.current.material as THREE.Material).dispose();
      pointsRef.current = null;
    }
    if (linksRef.current) {
      scene.remove(linksRef.current);
      linksRef.current.geometry.dispose();
      (linksRef.current.material as THREE.Material).dispose();
      linksRef.current = null;
    }

    const visiblePoints = points.filter(p => visibleTopics.has(p.topic));
    visiblePointsRef.current = visiblePoints;
    hoveredIndexRef.current = -1;
    setTooltip(null);
    if (!visiblePoints.length) return;

    const positions = new Float32Array(visiblePoints.length * 3);
    const colors = new Float32Array(visiblePoints.length * 3);
    const color = new THREE.Color();

    visiblePoints.forEach((p, i) => {
      positions[i * 3] = p.x;
      positions[i * 3 + 1] = p.y;
      positions[i * 3 + 2] = p.z;
      color.setHex(TOPIC_COLORS[p.topic] || TOPIC_COLORS.other);
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    });

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: POINT_SIZE_PX * window.devicePixelRatio,
      vertexColors: true,
      sizeAttenuation: false,
    });

    pointsRef.current = new THREE.Points(geometry, material);
    scene.add(pointsRef.current);

    if (showLinks) {
      const segments = buildNeighbourLinks(visiblePoints);
      if (segments.length) {
        const linkGeometry = new THREE.BufferGeometry();
        linkGeometry.setAttribute('position', new THREE.BufferAttribute(segments, 3));
        const linkMaterial = new THREE.LineBasicMaterial({
          color: (THEME_CONFIG[theme] || THEME_CONFIG.dark).link,
          transparent: true,
          opacity: 0.25,
        });
        linksRef.current = new THREE.LineSegments(linkGeometry, linkMaterial);
        scene.add(linksRef.current);
      }
    }
  }, [points, visibleTopics, showLinks, theme]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          flex: 1,
          display: 'block',
          cursor: tooltip ? 'pointer' : 'grab',
          width: '100%',
          height: '100%',
        }}
      />
      {tooltip && (
        <Box
          sx={{
            position: 'absolute',
            left: tooltip.x + 14,
            top: tooltip.y + 14,
            maxWidth: 320,
            p: 1.25,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 4,
            pointerEvents: 'none',
            zIndex: 2,
            // Flip to the left of the cursor near the right edge.
            transform: canvasRef.current && tooltip.x > canvasRef.current.clientWidth - 340 ? 'translateX(calc(-100% - 28px))' : undefined,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: `#${(TOPIC_COLORS[tooltip.point.topic] ?? TOPIC_COLORS.other).toString(16).padStart(6, '0')}`,
              }}
            />
            <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
              {tooltip.point.topic}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontFamily: 'monospace' }}>
              {(tooltip.point.id ?? '').substring(0, 8)}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 6,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {tooltip.point.document}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace' }}>
            {tooltip.point.x.toFixed(2)}, {tooltip.point.y.toFixed(2)}, {tooltip.point.z.toFixed(2)}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          position: 'absolute',
          bottom: 12,
          left: 12,
          fontSize: '0.75rem',
          color: 'text.secondary',
          pointerEvents: 'none',
        }}
      >
        Drag to rotate · Scroll to zoom · Shift+drag to pan · Double-click a point to focus
      </Box>
    </Box>
  );
});

VectorSpace3D.displayName = 'VectorSpace3D';
